import { ComponentType } from 'react';
import { ComponentWithAs, IconProps } from '@chakra-ui/react';

import { Device, DeviceType } from 'clipsal-cortex-types/src/api/api-site';

import { HOMEPAGE_WIDGETS } from './constants';
import { HomePageWidgetTileProps } from './HomePageWidgetTile';

const WIDGET_TYPE_TO_DEVICE_TYPE: Record<Exclude<WidgetType, 'LIVE_ENERGY' | 'SOLAR'>, DeviceType> = {
  ENERGY_MODE: 'INVERTER',
  SAVINGS: 'INVERTER',
  ECO_SCORE: 'INVERTER',
  BATTERY: 'BATTERY_PACK',
  EV_CHARGER: 'EV_CHARGER',
};

export type WidgetType = 'LIVE_ENERGY' | 'SOLAR' | 'ENERGY_MODE' | 'SAVINGS' | 'ECO_SCORE' | 'BATTERY' | 'EV_CHARGER';

export type HomepageWidget = {
  id: number;
  title: string;
  type: WidgetType;
  Icon: ComponentWithAs<'svg', IconProps>;
  link: string;
  status: string;
  Component: ComponentType<HomePageWidgetTileProps>;
};

/**
 * Parses a site's devices and returns Widget IDs for them
 *
 * @param devices - An array of devices
 * @param hasSolar - Boolean denoting if the site has solar or not
 */
export function getWidgetIDsForDevices(devices: Device[], hasSolar: boolean): number[] {
  return getWidgetsForDevices(devices, hasSolar).map((widget) => widget.id);
}

/**
 * Parses a site's devices and returns compatible widgets for them
 *
 * @param devices - An array of devices
 * @param hasSolar - Boolean denoting if the site has solar or not
 */
function getWidgetsForDevices(devices: Device[], hasSolar: boolean) {
  const deviceTypes = devices.map((d) => d.device_type);

  return HOMEPAGE_WIDGETS.filter((widget) => {
    switch (widget.type) {
      case 'LIVE_ENERGY':
        // Live energy widget is a special case in that is is compatible for sites with a meter or inverter
        return deviceTypes.includes('INVERTER') || deviceTypes.includes('METER');
      case 'SOLAR':
        // Solar widget is a special case since we need to check more than just the device type
        return deviceTypes.includes('INVERTER') && hasSolar;
      default:
        return deviceTypes.includes(WIDGET_TYPE_TO_DEVICE_TYPE[widget.type]);
    }
  });
}

/**
 * Returns Widgets from an array of Widget IDs in their order.
 *
 * @param widgetIDs - Array of Widget IDs
 */
function getWidgetsByIDs(widgetIDs: number[]) {
  const widgets: HomepageWidget[] = [];
  widgetIDs.forEach((cardId: number) => {
    const widget = HOMEPAGE_WIDGETS.find((card: HomepageWidget) => card.id === cardId);
    widget && widgets.push(widget);
  });
  return widgets;
}

/**
 * Returns Widget IDs from localStorage
 *
 * @param siteId - Site's identifier, used as a key to fetch from localStorage
 */
function getWidgetsFromStore(siteId: number) {
  const userCardSelection = localStorage.getItem(`homeGridItems_${siteId}`);
  let userCardSelectionArray: number[] = [];
  if (userCardSelection) {
    userCardSelectionArray = JSON.parse(userCardSelection);
  }
  return userCardSelectionArray;
}

/**
 * Returns Widgets from the store if they are valid (ie. the site has the device for a Widget)
 *
 * @param devices - An array of devices
 * @param siteId - Site's identifier, used as a key to fetch from localStorage
 * @param hasSolar - Boolean denoting if the site has solar or not
 */
export function getWidgets({ devices, siteId, hasSolar }: { devices: Device[]; siteId: number; hasSolar: boolean }) {
  const storedWidgetsById = getWidgetsFromStore(siteId);
  const compatibleWidgets = getWidgetsForDevices(devices, hasSolar);

  // Either nothing in the store, or they turned them all off -- display them all in both cases
  if (!storedWidgetsById.length) {
    return compatibleWidgets;
  } else {
    const storedWidgets = getWidgetsByIDs(storedWidgetsById);
    // Filter out any saved widgets that are not compatible with the current devices
    return storedWidgets.filter((widget) => {
      return compatibleWidgets.some((compatibleWidget) => compatibleWidget.id === widget.id);
    });
  }
}

/**
 * Returns Widget IDs from the store if they are compatible (ie. the site has the device for a Widget)
 *
 * @param devices - An array of devices
 * @param siteId - Site's identifier, used as a key to fetch from localStorage
 * @param hasSolar - Boolean denoting if the site has solar or not
 */
export function getWidgetIDs({ devices, siteId, hasSolar }: { devices: Device[]; siteId: number; hasSolar: boolean }) {
  return getWidgets({ devices, siteId, hasSolar }).map((w) => w.id);
}
